/**
 * The Apple Wallet lock-screen window (`relevanceMode` / `relevantStart` /
 * `relevantEnd` on a `WalletConfig`) — the pure rules, with NO imports, so the
 * template-transfer and AI-patch lanes can use them without pulling the
 * wallet builder's module graph into their own.
 *
 * The renderer is `PassJsonBuilder.relevanceWindow` in main-backend; every
 * rule here mirrors one there and is parity-tested against its test table.
 */
import type { WalletRelevanceMode } from "@/types/DocumentTemplate";

/** The three lock-screen timings the settings panel offers, in menu order. */
export const RELEVANCE_MODES: readonly WalletRelevanceMode[] = ["auto", "custom", "off"];

/**
 * The ONE shape a lock-screen bound may have — mirrored character for
 * character by the renderer's `PassJsonBuilder.STRICT_INSTANT`, and the parity
 * table in `getWalletBlockReason.test.ts` carries the same rows as
 * `PassJsonBuilderTest`. A zone is required: a zoneless string is read as UTC
 * by the picker and in the event's zone by the calendar lane, and `Date.parse`
 * alone would take prose ("June 18, 2026") the renderer does not. Years
 * 2000–2999 keep the window and the expiry derived from it (end + 24 h)
 * inside the four digits Apple parses. Seconds are optional on both sides.
 */
export const RELEVANCE_INSTANT =
  /^2\d{3}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\d|3[01])T(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d{1,9})?)?(?:Z|[+-](?:0\d|1[0-4]):[0-5]\d)$/;

/**
 * A bound as the renderer reads it, or null when it would not.
 *
 * The regex carries the RANGES (hour, minute, offset, years 2000–2999) because
 * V8's `Date.parse` rolls over what the JDK refuses: `24:00` becomes the next
 * day and 30 February becomes 2 March, so a bare `Date.parse` called a bound
 * readable that the renderer then dropped. The day-of-month is checked
 * against the real calendar for the same reason. Whole seconds: the renderer
 * drops the fraction before it compares the two bounds, so this does too.
 */
export function parseRelevanceInstant(raw: string | undefined | null): Date | null {
  const s = raw?.trim() ?? "";
  if (!RELEVANCE_INSTANT.test(s)) return null;
  const y = Number(s.slice(0, 4));
  const m = Number(s.slice(5, 7));
  const d = Number(s.slice(8, 10));
  if (new Date(Date.UTC(y, m - 1, d)).getUTCDate() !== d) return null;
  const ms = Date.parse(s);
  if (Number.isNaN(ms)) return null;
  return new Date(Math.floor(ms / 1000) * 1000);
}

/**
 * A stored bound in the form the picker reads (`…Z`), or "" when the renderer
 * could not read it either. An offset instant (`…+02:00`, written through
 * REST or the AI lane before normalisation existed) is a complete moment the
 * renderer honours; the picker's own parser appends `Z` blindly and would show
 * it as empty, so it is handed the same moment respelled.
 */
export function toRelevanceIso(raw: string | undefined | null): string {
  const d = parseRelevanceInstant(raw);
  return d ? d.toISOString() : "";
}

/**
 * The mode as the renderer reads it: trimmed and lower-cased, `auto` for
 * unset/blank. Anything else comes back AS TYPED so the panel can show it and
 * the operator can pick a real one — the renderer treats it as auto and notes
 * it on every send, and a panel that silently displayed "From the event" for
 * it would leave that note impossible to clear (picking the shown option is a
 * no-op in the dropdown).
 */
export function normalizeRelevanceMode(raw: string | undefined | null): WalletRelevanceMode | string {
  const s = raw?.trim() ?? "";
  if (!s) return "auto";
  const lower = s.toLowerCase();
  return (RELEVANCE_MODES as readonly string[]).includes(lower) ? lower : s;
}

/**
 * The config without the event-bound part of the lock-screen override — the
 * mirror of `WalletConfig.withoutRelevanceOverride()` in main-backend, for the
 * lanes that copy a config across events in the browser (template transfer,
 * "import from event" into a template). `off` rides: it is not bound to any
 * event. A `custom` without its bounds means nothing and reads as auto. A NEW
 * object: callers hold live react-query state by reference.
 */
export function withoutRelevanceOverride<T extends object>(cfg: T): T {
  const rest = { ...cfg } as Record<string, unknown>;
  const mode = normalizeRelevanceMode(typeof rest.relevanceMode === "string" ? rest.relevanceMode : undefined);
  delete rest.relevanceMode;
  delete rest.relevantStart;
  delete rest.relevantEnd;
  if (mode === "off") rest.relevanceMode = "off";
  return rest as T;
}
